import { Component, Input, Output, EventEmitter } from '@angular/core';

import { LucideAngularModule, Eraser, Settings, ClipboardCopy, ChartColumnIncreasing } from 'lucide-angular';

/**
 * Chat Info Bar Component - Settings and session info bar
 * Reduces HTML duplication between chat implementations
 */
@Component({
  selector: 'app-chat-info-bar',
  standalone: true,
  imports: [LucideAngularModule],
  template: `
    <div class="settings-info-bar flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 px-3 sm:px-4 py-2 rounded-t-lg border-b border-light-divider dark:border-dark-divider">
      <div class="text-xs text-light-text-secondary dark:text-dark-text-secondary w-full sm:w-auto">
        @if (whatsappNumber) {
          <div class="flex flex-wrap items-center gap-1.5">
            <!-- Custom info section slot -->
            <ng-content select="[infoContent]"></ng-content>
          </div>
        } @else {
          <span class="text-light-text-tertiary dark:text-dark-text-tertiary">Configuración requerida</span>
        }
      </div>
      <div class="flex items-center justify-end sm:justify-normal gap-1 sm:gap-2">
        <button
          (click)="onNewSession.emit()"
          class="settings-btn p-1.5 sm:p-2 rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-surface-variant text-light-text-secondary dark:text-dark-text-secondary hover:text-light-primary dark:hover:text-dark-primary transition-colors relative"
          [disabled]="!isSubscribed"
          title="Nueva sesión"
          aria-label="Comenzar nueva sesión"
        >
          <lucide-angular [img]="Eraser" class="w-4 h-4"></lucide-angular>
        </button>
        <button
          (click)="onSettings.emit(); $event.stopPropagation()"
          class="settings-btn p-1.5 sm:p-2 rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-surface-variant text-light-text-secondary dark:text-dark-text-secondary hover:text-light-primary dark:hover:text-dark-primary transition-colors relative"
          [class.settings-required]="!whatsappNumber"
          [title]="settingsTitle"
          [attr.aria-label]="settingsAriaLabel"
        >
          <lucide-angular [img]="Settings" class="w-4 h-4"></lucide-angular>
          @if (!whatsappNumber) {
            <span class="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-dark-surface"></span>
          }
        </button>
        <button
          (click)="onCopyHistory.emit()"
          class="settings-btn p-1.5 sm:p-2 rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-surface-variant text-light-text-secondary dark:text-dark-text-secondary hover:text-light-primary dark:hover:text-dark-primary transition-colors relative"
          title="Copiar historial"
          aria-label="Copiar historial"
        >
          <lucide-angular [img]="ClipboardCopy" class="w-4 h-4"></lucide-angular>
        </button>
        <button
          (click)="onOpenBenchmark.emit()"
          class="settings-btn p-1.5 sm:p-2 rounded-lg hover:bg-light-bg-secondary dark:hover:bg-dark-surface-variant text-light-text-secondary dark:text-dark-text-secondary hover:text-light-primary dark:hover:text-dark-primary transition-colors relative opacity-80 hover:opacity-100"
          [disabled]="!canOpenBenchmark"
          [title]="benchmarkTitle"
          [attr.aria-label]="benchmarkAriaLabel"
        >
          <lucide-angular [img]="Benchmark" class="w-4 h-4"></lucide-angular>
        </button>
      </div>
    </div>
  `,
})
export class ChatInfoBarComponent {
  @Input() whatsappNumber?: string;
  @Input() isSubscribed: boolean = false;
  @Input() settingsTitle: string = 'Configuración';
  @Input() settingsAriaLabel: string = 'Configuración';
  @Input() canOpenBenchmark: boolean = false;
  @Input() benchmarkTitle: string = 'Ver benchmark';
  @Input() benchmarkAriaLabel: string = 'Ver benchmark de conversación';
  
  @Output() onNewSession = new EventEmitter<void>();
  @Output() onSettings = new EventEmitter<void>();
  @Output() onCopyHistory = new EventEmitter<void>();
  @Output() onOpenBenchmark = new EventEmitter<void>();

  readonly Eraser = Eraser;
  readonly Settings = Settings;
  readonly ClipboardCopy = ClipboardCopy;
  readonly Benchmark = ChartColumnIncreasing;
}
